import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import GigRequestConversationModal from "../../../components/gigs/GigRequestConversationModal";
import { ScheduleProposalForm } from "../../../components/gigs/ScheduleProposalModal";
import TeenRequestProfileCard from "../../../components/adult/gigs/TeenRequestProfileCard";
import TeenRequestsPanel from "../../../components/adult/gigs/TeenRequestsPanel";
import {
  formatDateRangeWithWeekdays,
  formatGigSchedule,
  getTeenName,
} from "../../../components/adult/gigs/gigDisplay";
import Button from "../../../components/ui/Button/Button";
import Modal from "../../../components/ui/Modal/Modal";
import PageActionBar from "../../../components/ui/Page/PageActionBar";
import PageBackButton from "../../../components/ui/Page/PageBackButton";
import PageHero from "../../../components/ui/Page/PageHero";
import PageSectionCard from "../../../components/ui/Page/PageSectionCard";
import PageShell from "../../../components/ui/Page/PageShell";
import Toast, { type ToastTone } from "../../../components/ui/Toast/Toast";
import {
  captureAdultScheduleProposalSent,
  captureGigFundingCheckoutStarted,
  captureTeenAcceptedForGig,
  captureTeenRejectedForGig,
} from "../../../lib/analytics";
import { gigsApi } from "../../../lib/gigs";
import type {
  AdultGigRequestListItemResponse,
  AdultGigRequestTeenDetailResponse,
  ScheduleProposalResponse,
  ScheduleProposalRequest,
} from "../../../types/gigTypes";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import { formatUsdFromCents } from "../../../utils/CurrencyUtils";
import { formatGigCompensation } from "../../../utils/GigCompensationUtils";
import { formatGigCrewSummary, getConfirmedSeatCount, getSelectedSeatCount, getSpotsRemaining } from "../../../utils/GigCrewUtils";
import { formatCityStateZip } from "../../../utils/LocationUtils";
import { postedGigStatusClassName, postedGigStatusLabel } from "../../../components/adult/dashboard/shared/dashboardDisplay";

const ACTIVE_APPLICANT_STATUSES = new Set<AdultGigRequestListItemResponse["status"]>([
  "POSTER_PENDING",
  "POSTER_SELECTED_PENDING_CREW",
  "PAYOUT_SETUP_PENDING",
  "FUNDING_PENDING",
]);

const getGigReviewState = (requests: AdultGigRequestListItemResponse[]) => {
  const hasPosterPendingRequests = requests.some((request) => request.status === "POSTER_PENDING");
  const hasVisibleApplicantRequests = requests.some((request) => (
    request.status === "GUARDIAN_PENDING"
    || ACTIVE_APPLICANT_STATUSES.has(request.status)
    || request.status === "POSTER_ACCEPTED"
    || request.status === "FILLED_BY_OTHER_TEEN"
    || request.status === "GUARDIAN_REJECTED"
    || request.status === "POSTER_REJECTED"
    || request.status === "WITHDRAWN"
  ));
  const hasActiveApplicantRequests = requests.some((request) => ACTIVE_APPLICANT_STATUSES.has(request.status));
  const hasAcceptedRequest = requests.some((request) => request.status === "POSTER_ACCEPTED");

  return {
    hasPosterPendingRequests,
    hasPendingRequests: hasActiveApplicantRequests,
    hasVisibleApplicantRequests,
    hasAcceptedRequest,
  };
};

const getConversationScheduleProposals = (
  request: AdultGigRequestListItemResponse | null,
): ScheduleProposalResponse[] | null => {
  if (!request) {
    return null;
  }

  const scheduleProposals = request.scheduleProposals ?? [];
  if (scheduleProposals.length > 0) {
    return scheduleProposals;
  }

  if (!request.proposedStartTime || !request.proposedEndTime) {
    return scheduleProposals;
  }

  return [{
    id: `initial-${request.requestId}`,
    actorRole: "TEEN",
    actorUserId: request.teenUserId,
    note: request.message,
    proposedStartTime: request.proposedStartTime,
    proposedEndTime: request.proposedEndTime,
    createdAt: request.createdAt,
  }];
};

const scrollToTeenRequestCard = (requestId: string, attempt = 0) => {
  const targetElement = document.getElementById(`adult-teen-request-${requestId}`);
  if (!targetElement) {
    if (attempt < 8) {
      window.setTimeout(() => scrollToTeenRequestCard(requestId, attempt + 1), 60);
    }
    return;
  }

  targetElement.scrollIntoView?.({
    block: "center",
    behavior: "smooth",
  });
};

type ToastState = { message: string; tone: ToastTone } | null;
type PendingFundingState = {
  gigId: string;
  gigTitle: string;
  compensationCents: number;
  request: AdultGigRequestListItemResponse;
};
type PendingCombinedFundingState = {
  gigId: string;
  gigTitle: string;
  selectedSeatCount: number;
  posterPaymentPerTeenCents: number | null;
};
type PendingVolunteerAcceptanceState = {
  gigId: string;
  gigTitle: string;
  request: AdultGigRequestListItemResponse;
};
type PendingSelectionState = {
  gigId: string;
  gigTitle: string;
  request: AdultGigRequestListItemResponse;
  spotsRemaining: number;
};
type PendingRejectionState = {
  gigId: string;
  gigTitle: string;
  request: AdultGigRequestListItemResponse;
};
type PendingProceedWithSelectedCrewState = {
  gigId: string;
  gigTitle: string;
  originalTeensRequired: number;
  crewSeatCount: number;
  spotsRemaining: number;
  projectedTeenPayoutCents: number | null;
  mode: "selected" | "remaining";
};

const AdultGigApplicantsPage = () => {
  const posthog = usePostHog();
  const { gigId } = useParams<{ gigId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [toast, setToast] = useState<ToastState>(null);
  const [error, setError] = useState<string | null>(null);
  const [teenDetailsByRequestId, setTeenDetailsByRequestId] = useState<Record<string, AdultGigRequestTeenDetailResponse>>({});
  const [loadingTeenRequestIds, setLoadingTeenRequestIds] = useState<string[]>([]);
  const [approvingRequestId, setApprovingRequestId] = useState<string | null>(null);
  const [rejectingRequestId, setRejectingRequestId] = useState<string | null>(null);
  const [schedulingRequestId, setSchedulingRequestId] = useState<string | null>(null);
  const [isScheduleProposalComposerOpen, setIsScheduleProposalComposerOpen] = useState(false);
  const [scheduleProposalError, setScheduleProposalError] = useState<string | null>(null);
  const [pendingFunding, setPendingFunding] = useState<PendingFundingState | null>(null);
  const [pendingCombinedFunding, setPendingCombinedFunding] = useState<PendingCombinedFundingState | null>(null);
  const [pendingVolunteerAcceptance, setPendingVolunteerAcceptance] = useState<PendingVolunteerAcceptanceState | null>(null);
  const [pendingSelection, setPendingSelection] = useState<PendingSelectionState | null>(null);
  const [pendingRejection, setPendingRejection] = useState<PendingRejectionState | null>(null);
  const [pendingProceedWithSelectedCrew, setPendingProceedWithSelectedCrew] = useState<PendingProceedWithSelectedCrewState | null>(null);
  const [fundingCheckoutError, setFundingCheckoutError] = useState<string | null>(null);
  const [fundingAgreementAccepted, setFundingAgreementAccepted] = useState(false);
  const [isStartingFundingCheckout, setIsStartingFundingCheckout] = useState(false);
  const [isProceedingWithSelectedCrew, setIsProceedingWithSelectedCrew] = useState(false);
  const [selectedTeenRequest, setSelectedTeenRequest] = useState<AdultGigRequestListItemResponse | null>(null);
  const [selectedConversationRequest, setSelectedConversationRequest] = useState<AdultGigRequestListItemResponse | null>(null);
  const [highlightedRequestId, setHighlightedRequestId] = useState<string | null>(null);
  const gigsQuery = useQuery({
    queryKey: ["adult-posted-gigs"],
    queryFn: () => gigsApi.listAdultPostedGigs(),
  });
  const requestsQuery = useQuery({
    queryKey: ["adult-gig-applicants", gigId],
    queryFn: () => gigsApi.listAdultRequestsForGig(gigId!),
    enabled: Boolean(gigId),
    refetchInterval: 15000,
  });

  const gig = useMemo(
    () => gigsQuery.data?.find((currentGig) => currentGig.id === gigId) ?? null,
    [gigsQuery.data, gigId],
  );
  const requests = useMemo(
    () => requestsQuery.data ?? [],
    [requestsQuery.data],
  );
  const visibleProfileRequests = useMemo(
    () => requests.filter((request) => (
      request.status === "GUARDIAN_PENDING"
      || ACTIVE_APPLICANT_STATUSES.has(request.status)
      || request.status === "POSTER_ACCEPTED"
    )),
    [requests],
  );
  const { hasPendingRequests, hasVisibleApplicantRequests, hasAcceptedRequest } = useMemo(
    () => getGigReviewState(requests),
    [requests],
  );
  const isMultiTeenGig = (gig?.teensRequired ?? 1) > 1;
  const selectedSeatCount = gig ? getSelectedSeatCount(gig) : 0;
  const confirmedSeatCount = gig ? getConfirmedSeatCount(gig) : 0;
  const spotsRemaining = gig ? getSpotsRemaining(gig) : 0;
  const hasOpenReplacementSeat = gig !== null
    && isMultiTeenGig
    && Boolean(gig.fundedOpen)
    && confirmedSeatCount > 0
    && spotsRemaining > 0;
  const applicantActionsOpen = gig !== null
    && gig.status === "PUBLISHED"
    && (!hasAcceptedRequest || hasOpenReplacementSeat);
  const selectsBeforePayment = gig !== null && isMultiTeenGig && gig.status === "PUBLISHED" && !gig.fundedOpen;

  const loadTeenDetail = useCallback(async (requestId: string, options?: { suppressError?: boolean }) => {
    if (!gigId || teenDetailsByRequestId[requestId] || loadingTeenRequestIds.includes(requestId)) {
      return;
    }

    setLoadingTeenRequestIds((current) => (
      current.includes(requestId) ? current : [...current, requestId]
    ));
    try {
      const teenDetail = await gigsApi.getAdultTeenDetailForGigRequest(gigId, requestId);
      setTeenDetailsByRequestId((current) => ({
        ...current,
        [requestId]: teenDetail,
      }));
    } catch (requestError) {
      if (!options?.suppressError) {
        setError(apiErrorToMessage(requestError, "Failed to load teen details."));
      }
    } finally {
      setLoadingTeenRequestIds((current) => current.filter((currentRequestId) => currentRequestId !== requestId));
    }
  }, [gigId, loadingTeenRequestIds, teenDetailsByRequestId]);

  useEffect(() => {
    visibleProfileRequests.forEach((request) => {
      void loadTeenDetail(request.requestId, { suppressError: true });
    });
  }, [loadTeenDetail, visibleProfileRequests]);

  useEffect(() => {
    if (!gigId || requests.length === 0) {
      return;
    }

    const requestId = searchParams.get("requestId");
    const section = searchParams.get("section");
    if (!requestId) {
      return;
    }

    const targetRequest = requests.find((request) => request.requestId === requestId) ?? null;
    if (!targetRequest) {
      return;
    }

    if (section === "schedule") {
      setHighlightedRequestId(null);
      setSelectedConversationRequest(targetRequest);
      setSelectedTeenRequest(null);
      return;
    }

    if (section === "funding") {
      setHighlightedRequestId(requestId);
      setSelectedConversationRequest(null);
      setSelectedTeenRequest(null);
      scrollToTeenRequestCard(requestId);
      return;
    }

    setHighlightedRequestId(null);
    setSelectedTeenRequest(targetRequest);
    setSelectedConversationRequest(null);
    void loadTeenDetail(requestId);
  }, [gigId, loadTeenDetail, requests, searchParams]);

  useEffect(() => {
    if (selectedTeenRequest) {
      const refreshedRequest = requests.find((request) => request.requestId === selectedTeenRequest.requestId) ?? null;
      setSelectedTeenRequest(refreshedRequest);
    }

    if (selectedConversationRequest) {
      const refreshedRequest = requests.find((request) => request.requestId === selectedConversationRequest.requestId) ?? null;
      setSelectedConversationRequest(refreshedRequest);
    }
  }, [requests, selectedConversationRequest, selectedTeenRequest]);

  if (!gigId) {
    return <Navigate to="/adult/gigs" replace />;
  }

  const refreshData = async () => {
    const [, updatedRequestsResult] = await Promise.all([
      gigsQuery.refetch(),
      requestsQuery.refetch(),
    ]);

    return updatedRequestsResult.data ?? [];
  };

  const handleOpenTeenModal = (request: AdultGigRequestListItemResponse) => {
    setToast(null);
    setSelectedConversationRequest(null);
    setSelectedTeenRequest(request);
    void loadTeenDetail(request.requestId);
  };

  const handleOpenConversationModal = (request: AdultGigRequestListItemResponse) => {
    setToast(null);
    setSelectedTeenRequest(null);
    setSelectedConversationRequest(request);
  };

  const handleAcceptTeen = async (_gigId: string, request: AdultGigRequestListItemResponse) => {
    if (!gig) {
      setError("Gig not found for this request.");
      return;
    }

    if (isMultiTeenGig && !gig.fundedOpen) {
      setPendingSelection({
        gigId,
        gigTitle: gig.title,
        request,
        spotsRemaining: getSpotsRemaining(gig),
      });
      return;
    }

    if (!gig.volunteer) {
      if (gig.fundedOpen) {
        setApprovingRequestId(request.requestId);
        setToast(null);
        setError(null);
        try {
          await gigsApi.createPosterDecision(request.requestId, {
            decision: "ACCEPTED",
            message: request.message,
            confirmedStartTime: request.proposedStartTime,
            confirmedEndTime: request.proposedEndTime,
          });
          captureTeenAcceptedForGig(posthog, {
            gig_id: gigId,
            request_id: request.requestId,
            gig_type: "funded_open",
          });
          navigate(`/adult/scheduled-gigs?requestId=${request.requestId}`);
        } catch (requestError) {
          setError(apiErrorToMessage(requestError, "Failed to accept teen for this funded gig."));
        } finally {
          setApprovingRequestId(null);
        }
        return;
      }

      if (gig.paymentAmountCents === null) {
        setError("This paid gig is missing a payment amount.");
        return;
      }

      setFundingAgreementAccepted(false);
      setPendingFunding({
        gigId,
        gigTitle: gig.title,
        compensationCents: gig.paymentAmountCents,
        request,
      });
      return;
    }

    setPendingVolunteerAcceptance({
      gigId,
      gigTitle: gig.title,
      request,
    });
  };

  const handleConfirmSelection = async () => {
    if (!pendingSelection) {
      return;
    }
    const currentGig = gig;
    if (!currentGig) {
      setError("Gig not found for this request.");
      return;
    }

    setApprovingRequestId(pendingSelection.request.requestId);
    setToast(null);
    setError(null);

    try {
      await gigsApi.createPosterDecision(pendingSelection.request.requestId, {
        decision: "ACCEPTED",
        message: pendingSelection.request.message,
        confirmedStartTime: pendingSelection.request.proposedStartTime,
        confirmedEndTime: pendingSelection.request.proposedEndTime,
      });
      captureTeenAcceptedForGig(posthog, {
        gig_id: pendingSelection.gigId,
        request_id: pendingSelection.request.requestId,
        gig_type: "multi_teen_selection",
      });

      await refreshData();
      setSelectedTeenRequest((current) => (
        current?.requestId === pendingSelection.request.requestId ? null : current
      ));
      setSelectedConversationRequest((current) => (
        current?.requestId === pendingSelection.request.requestId ? null : current
      ));
      const remainingAfterSelection = Math.max(0, pendingSelection.spotsRemaining - 1);
      setPendingSelection(null);
      if (remainingAfterSelection === 0 && !currentGig.volunteer) {
        setIsStartingFundingCheckout(true);
        try {
          const response = await gigsApi.createCombinedFundingCheckoutSession(pendingSelection.gigId, {
            agreementAccepted: true,
          });
          if (!response.checkoutRequired) {
            setToast({
              tone: "info",
              message: response.message ?? "Crew selected. Payment will be available after payout setup is complete.",
            });
            return;
          }
          if (!response.checkoutUrl || response.amountCents === null) {
            setError("Crew selected, but Stripe checkout is not available yet. Refresh this page and try again.");
            return;
          }
          captureGigFundingCheckoutStarted(posthog, {
            gig_id: pendingSelection.gigId,
            request_id: null,
            compensation_cents: response.amountCents,
            checkout_type: "combined_crew",
            selected_seat_count: currentGig.teensRequired ?? 1,
          });
          window.location.assign(response.checkoutUrl);
        } catch (checkoutError) {
          setError(`Crew selected, but checkout could not start: ${apiErrorToMessage(checkoutError, "Failed to start Stripe checkout for this gig.")}`);
        } finally {
          setIsStartingFundingCheckout(false);
        }
        return;
      } else {
        setToast({
          tone: "success",
          message: remainingAfterSelection > 0
            ? `Applicant selected. ${remainingAfterSelection} spot${remainingAfterSelection === 1 ? "" : "s"} still open.`
            : "Applicant selected. All requested spots are now selected.",
        });
      }
    } catch (requestError) {
      setError(apiErrorToMessage(requestError, "Failed to select applicant for this gig."));
    } finally {
      setApprovingRequestId(null);
    }
  };

  const handleRejectTeen = (_gigId: string, request: AdultGigRequestListItemResponse) => {
    if (!gig) {
      setError("Gig not found for this request.");
      return;
    }

    setError(null);
    setToast(null);
    setPendingRejection({
      gigId,
      gigTitle: gig.title,
      request,
    });
  };

  const handleConfirmRejection = async () => {
    if (!pendingRejection) {
      return;
    }

    setRejectingRequestId(pendingRejection.request.requestId);
    setToast(null);
    setError(null);

    try {
      await gigsApi.createPosterDecision(pendingRejection.request.requestId, {
        decision: "REJECTED",
        message: pendingRejection.request.message,
      });
      captureTeenRejectedForGig(posthog, {
        gig_id: pendingRejection.gigId,
        request_id: pendingRejection.request.requestId,
      });

      const updatedRequests = await refreshData();
      setSelectedTeenRequest((current) => (
        current?.requestId === pendingRejection.request.requestId ? null : current
      ));
      setSelectedConversationRequest((current) => (
        current?.requestId === pendingRejection.request.requestId ? null : current
      ));
      setPendingRejection(null);
      const removedSelection = pendingRejection.request.status === "POSTER_SELECTED_PENDING_CREW";
      setToast({
        tone: "success",
        message: removedSelection
          ? "Selection removed. The gig can keep looking for another applicant."
          : updatedRequests.some((request) => ACTIVE_APPLICANT_STATUSES.has(request.status) || request.status === "GUARDIAN_PENDING")
            ? "Teen rejected for this gig."
            : "Teen rejected. No active applicants remain for this gig.",
      });
    } catch (requestError) {
      setError(apiErrorToMessage(requestError, "Failed to reject teen for this gig."));
    } finally {
      setRejectingRequestId(null);
    }
  };

  const handleOpenProceedWithSelectedCrew = (mode: PendingProceedWithSelectedCrewState["mode"] = "selected") => {
    if (!gig) {
      setError("Gig not found.");
      return;
    }

    const crewSeatCount = mode === "remaining" ? getConfirmedSeatCount(gig) : getSelectedSeatCount(gig);
    const originalTeensRequired = gig.teensRequired ?? 1;
    const projectedTeenPayoutCents = mode === "remaining"
      && gig.paymentAmountCents !== null
      && crewSeatCount > 0
      ? Math.floor((gig.paymentAmountCents * originalTeensRequired) / crewSeatCount)
      : null;
    setError(null);
    setToast(null);
    setPendingProceedWithSelectedCrew({
      gigId,
      gigTitle: gig.title,
      originalTeensRequired,
      crewSeatCount,
      spotsRemaining: getSpotsRemaining(gig),
      projectedTeenPayoutCents,
      mode,
    });
  };

  const handleConfirmProceedWithSelectedCrew = async () => {
    if (!pendingProceedWithSelectedCrew) {
      return;
    }
    const currentGig = gig;
    if (!currentGig) {
      setError("Gig not found.");
      return;
    }

    setIsProceedingWithSelectedCrew(true);
    setToast(null);
    setError(null);

    try {
      const response = await gigsApi.proceedWithSelectedCrew(pendingProceedWithSelectedCrew.gigId);
      await refreshData();
      setPendingProceedWithSelectedCrew(null);
      if (pendingProceedWithSelectedCrew.mode === "selected" && !currentGig.volunteer && response.spotsRemaining === 0) {
        setFundingAgreementAccepted(false);
        setPendingCombinedFunding({
          gigId: pendingProceedWithSelectedCrew.gigId,
          gigTitle: pendingProceedWithSelectedCrew.gigTitle,
          selectedSeatCount: response.selectedSeatCount,
          posterPaymentPerTeenCents: currentGig.posterPaymentAmountCents ?? null,
        });
      } else if (pendingProceedWithSelectedCrew.mode === "remaining") {
        navigate("/adult/scheduled-gigs");
      } else {
        const updatedCrewCount = response.newTeensRequired;
        setToast({
          tone: "success",
          message: `Crew size updated to ${updatedCrewCount} selected teen${updatedCrewCount === 1 ? "" : "s"}.`,
        });
      }
    } catch (requestError) {
      setError(apiErrorToMessage(requestError, "Failed to update the requested crew size."));
    } finally {
      setIsProceedingWithSelectedCrew(false);
    }
  };

  const handleConfirmVolunteerAcceptance = async () => {
    if (!pendingVolunteerAcceptance) {
      return;
    }

    setApprovingRequestId(pendingVolunteerAcceptance.request.requestId);
    setToast(null);
    setError(null);

    try {
      await gigsApi.createPosterDecision(pendingVolunteerAcceptance.request.requestId, {
        decision: "ACCEPTED",
        message: pendingVolunteerAcceptance.request.message,
        confirmedStartTime: pendingVolunteerAcceptance.request.proposedStartTime,
        confirmedEndTime: pendingVolunteerAcceptance.request.proposedEndTime,
      });
      captureTeenAcceptedForGig(posthog, {
        gig_id: pendingVolunteerAcceptance.gigId,
        request_id: pendingVolunteerAcceptance.request.requestId,
        gig_type: "volunteer",
      });
      navigate(`/adult/scheduled-gigs?requestId=${pendingVolunteerAcceptance.request.requestId}&section=messages`);
    } catch (requestError) {
      setError(apiErrorToMessage(requestError, "Failed to accept teen for this gig."));
    } finally {
      setApprovingRequestId(null);
    }
  };

  const handleStartFundingCheckout = async () => {
    if (pendingCombinedFunding) {
      setIsStartingFundingCheckout(true);
      setToast(null);
      setError(null);
      setFundingCheckoutError(null);

      try {
        const response = await gigsApi.createCombinedFundingCheckoutSession(pendingCombinedFunding.gigId, {
          agreementAccepted: fundingAgreementAccepted,
        });
        if (!response.checkoutRequired) {
          await refreshData();
          setPendingCombinedFunding(null);
          setToast({
            tone: "info",
            message: response.message ?? "Crew selected. Payment will be available after payout setup is complete.",
          });
          return;
        }
        if (!response.checkoutUrl || response.amountCents === null) {
          setFundingCheckoutError("Stripe checkout is not available yet. Refresh this page and try again.");
          return;
        }
        captureGigFundingCheckoutStarted(posthog, {
          gig_id: pendingCombinedFunding.gigId,
          request_id: null,
          compensation_cents: response.amountCents,
          checkout_type: "combined_crew",
          selected_seat_count: pendingCombinedFunding.selectedSeatCount,
        });
        setPendingCombinedFunding(null);
        window.location.assign(response.checkoutUrl);
      } catch (requestError) {
        setFundingCheckoutError(apiErrorToMessage(requestError, "Failed to start Stripe checkout for this gig."));
      } finally {
        setIsStartingFundingCheckout(false);
      }
      return;
    }

    if (!pendingFunding) {
      return;
    }

    setIsStartingFundingCheckout(true);
    setToast(null);
    setError(null);
    setFundingCheckoutError(null);

    try {
      const response = await gigsApi.createFundingCheckoutSession(pendingFunding.request.requestId, {
        agreementAccepted: fundingAgreementAccepted,
        confirmedStartTime: pendingFunding.request.proposedStartTime,
        confirmedEndTime: pendingFunding.request.proposedEndTime,
      });
      if (!response.checkoutRequired) {
        await refreshData();
        setPendingFunding(null);
        setToast({
          tone: "info",
          message: response.message ?? "Teen selected. Payment will be available after payout setup is complete.",
        });
        return;
      }
      if (!response.checkoutUrl) {
        setFundingCheckoutError("Stripe checkout is not available yet. Refresh this page and try again.");
        return;
      }
      captureGigFundingCheckoutStarted(posthog, {
        gig_id: pendingFunding.gigId,
        request_id: pendingFunding.request.requestId,
        compensation_cents: pendingFunding.compensationCents,
        checkout_type: "single_request",
      });
      setPendingFunding(null);
      window.location.assign(response.checkoutUrl);
    } catch (requestError) {
      setFundingCheckoutError(apiErrorToMessage(requestError, "Failed to start Stripe checkout for this gig."));
    } finally {
      setIsStartingFundingCheckout(false);
    }
  };

  const handleSubmitScheduleProposal = async (payload: ScheduleProposalRequest) => {
    if (!selectedConversationRequest) {
      return;
    }

    setSchedulingRequestId(selectedConversationRequest.requestId);
    setToast(null);
    setError(null);
    setScheduleProposalError(null);

    try {
      await gigsApi.submitAdultScheduleProposal(selectedConversationRequest.requestId, payload);
      captureAdultScheduleProposalSent(posthog, { request_id: selectedConversationRequest.requestId, gig_id: gigId });
      const updatedRequests = await refreshData();
      setSelectedConversationRequest(
        updatedRequests.find((request) => request.requestId === selectedConversationRequest.requestId) ?? selectedConversationRequest,
      );
      setIsScheduleProposalComposerOpen(false);
      setToast({ tone: "success", message: "Updated proposed time sent to the teen." });
    } catch (requestError) {
      setScheduleProposalError(apiErrorToMessage(requestError, "Failed to send the updated proposed time."));
    } finally {
      setSchedulingRequestId(null);
    }
  };

  if (gigsQuery.isLoading || requestsQuery.isLoading) {
    return (
      <PageShell width="6xl">
        <PageBackButton fallbackTo="/adult/gigs" label="Back to Posted Gigs" className="mb-4" />
        <PageSectionCard className="p-5">
          <p className="text-sm text-myteengig-text-muted">Loading applicant details...</p>
        </PageSectionCard>
      </PageShell>
    );
  }

  if (gigsQuery.error || requestsQuery.error) {
    return (
      <PageShell width="6xl">
        <PageBackButton fallbackTo="/adult/gigs" label="Back to Posted Gigs" className="mb-4" />
        <PageSectionCard className="border-rose-200 bg-rose-50/70 p-5">
          <p className="text-sm text-rose-700">
            {apiErrorToMessage(gigsQuery.error ?? requestsQuery.error, "Failed to load this gig's applicants.")}
          </p>
        </PageSectionCard>
      </PageShell>
    );
  }

  if (!gig) {
    return (
      <PageShell width="6xl">
        <PageBackButton fallbackTo="/adult/gigs" label="Back to Posted Gigs" className="mb-4" />
        <PageSectionCard className="p-5">
          <p className="text-sm text-myteengig-text-muted">
            This gig is no longer available in your posted gigs workflow.
          </p>
        </PageSectionCard>
      </PageShell>
    );
  }

  const listingStatusText = gig.status === "PUBLISHED" && !hasPendingRequests
    ? "Published"
    : postedGigStatusLabel[gig.status];
  const selectedConversationProposals = getConversationScheduleProposals(selectedConversationRequest);
  const removingSelectedApplicant = pendingRejection?.request.status === "POSTER_SELECTED_PENDING_CREW";
  const canProceedWithSelectedCrew = selectsBeforePayment
    && selectedSeatCount > 0
    && spotsRemaining > 0
    && !hasAcceptedRequest;
  const canProceedWithRemainingCrew = hasOpenReplacementSeat;
  const canStartCombinedFundingCheckout = selectsBeforePayment
    && selectedSeatCount > 0
    && spotsRemaining === 0
    && !hasAcceptedRequest
    && gig.fundingStatus !== "CHECKOUT_PENDING";
  const combinedFundingTotalChargeCents = pendingCombinedFunding?.posterPaymentPerTeenCents !== null
    && pendingCombinedFunding?.posterPaymentPerTeenCents !== undefined
    ? pendingCombinedFunding.posterPaymentPerTeenCents * pendingCombinedFunding.selectedSeatCount
    : null;
  const pendingSelectionRemainingAfterSelection = pendingSelection
    ? Math.max(0, pendingSelection.spotsRemaining - 1)
    : null;
  const pendingSelectionCrew = pendingSelection
    ? [
      ...requests.filter((request) => request.status === "POSTER_SELECTED_PENDING_CREW"),
      pendingSelection.request,
    ]
    : [];
  const pendingSelectionScheduleLabel = pendingSelection
    ? formatDateRangeWithWeekdays(
      pendingSelection.request.proposedStartTime,
      pendingSelection.request.proposedEndTime,
      pendingSelection.request.gigTimeZone,
    )
    : "";

  return (
    <PageShell width="6xl">
      <Toast
        isOpen={toast !== null}
        tone={toast?.tone ?? "info"}
        message={toast?.message ?? ""}
        onClose={() => setToast(null)}
      />
      <PageBackButton fallbackTo="/adult/gigs" label="Back to Posted Gigs" className="mb-4" />

      <PageHero
        title={<span className="text-2xl sm:text-3xl">Applicants</span>}
        description={
          applicantActionsOpen
            ? `Review applicants for ${gig.title}.`
            : `Review applicant details for ${gig.title}. Applicant actions are no longer available for this gig.`
        }
        className="bg-gradient-to-br from-white via-myteengig-background to-white py-3 sm:py-4 lg:py-4"
        contentClassName="[&_h1]:mt-0 [&_h1+div]:mt-3"
        footer={(
          <div className="flex flex-wrap items-center justify-center gap-2 text-sm text-myteengig-text-muted">
            <span className="rounded-full border border-myteengig-border bg-white px-3 py-1 font-medium text-myteengig-text-primary">
              {formatGigCompensation(gig.paymentAmountCents, gig.volunteer)}
            </span>
            <span className="rounded-full border border-myteengig-border bg-white px-3 py-1 font-medium text-myteengig-text-primary">
              {formatGigCrewSummary(gig)}
            </span>
            <span className="rounded-full border border-myteengig-border bg-white px-3 py-1 font-medium text-myteengig-text-primary">
              {formatGigSchedule(gig)}
            </span>
            <span className="rounded-full border border-myteengig-border bg-white px-3 py-1 font-medium text-myteengig-text-primary">
              {formatCityStateZip(gig)}
            </span>
            <span className={`rounded-full border px-3 py-1 font-medium ${postedGigStatusClassName[gig.status]}`}>
              {listingStatusText}
            </span>
          </div>
        )}
      />

      {error ? (
        <PageSectionCard className="mt-5 border-rose-200 bg-rose-50/70 p-5">
          <p className="text-sm text-rose-700">{error}</p>
        </PageSectionCard>
      ) : null}

      {canProceedWithSelectedCrew ? (
        <PageSectionCard className="mt-5 border-sky-200 bg-sky-50/80 p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-sky-950">
                {selectedSeatCount} of {gig.teensRequired ?? 1} teens selected
              </p>
              <p className="mt-1 text-sm text-sky-900">
                Change the requested crew size to the selected applicants.
              </p>
            </div>
            <Button
              onClick={() => handleOpenProceedWithSelectedCrew("selected")}
              className="w-full sm:w-auto"
            >
              Proceed with {selectedSeatCount} teen{selectedSeatCount === 1 ? "" : "s"}
            </Button>
          </div>
        </PageSectionCard>
      ) : null}

      {canProceedWithRemainingCrew ? (
        <PageSectionCard className="mt-5 border-sky-200 bg-sky-50/80 p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-sky-950">
                {confirmedSeatCount} of {gig.teensRequired ?? 1} teens are still active
              </p>
              <p className="mt-1 text-sm text-sky-900">
                Continue with the active crew and split the funded payout across them, or accept another applicant to fill the open role.
              </p>
            </div>
            <Button
              onClick={() => handleOpenProceedWithSelectedCrew("remaining")}
              className="w-full sm:w-auto"
            >
              Continue with {confirmedSeatCount} teen{confirmedSeatCount === 1 ? "" : "s"}
            </Button>
          </div>
        </PageSectionCard>
      ) : null}

      {canStartCombinedFundingCheckout ? (
        <PageSectionCard className="mt-5 border-emerald-200 bg-emerald-50/80 p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-emerald-950">
                {selectedSeatCount} of {gig.teensRequired ?? 1} teens selected
              </p>
              <p className="mt-1 text-sm text-emerald-900">
                Start one checkout for the selected crew.
              </p>
            </div>
            <Button
              onClick={() => {
                setFundingAgreementAccepted(false);
                setFundingCheckoutError(null);
                setPendingCombinedFunding({
                  gigId,
                  gigTitle: gig.title,
                  selectedSeatCount,
                  posterPaymentPerTeenCents: gig.posterPaymentAmountCents ?? null,
                });
              }}
              className="w-full sm:w-auto"
            >
              Start checkout
            </Button>
          </div>
        </PageSectionCard>
      ) : null}

      {selectsBeforePayment && gig.fundingStatus === "CHECKOUT_PENDING" ? (
        <PageSectionCard className="mt-5 border-amber-200 bg-amber-50/80 p-5">
          <p className="text-sm font-semibold text-amber-950">Checkout is in progress</p>
          <p className="mt-1 text-sm text-amber-900">
            The selected crew will be confirmed after Stripe payment succeeds.
          </p>
        </PageSectionCard>
      ) : null}

      <section className="mt-5">
        {!hasVisibleApplicantRequests ? (
          <div className="rounded-2xl border border-dashed border-myteengig-border bg-myteengig-background p-6">
            <p className="font-medium text-myteengig-text-primary">No applicants are waiting on your decision</p>
            <p className="mt-2 text-sm text-myteengig-text-muted">
              Applicants will appear here after parent or guardian approval is complete and the request reaches your review flow.
            </p>
          </div>
        ) : (
          <TeenRequestsPanel
            gigId={gig.id}
            requiresFunding={!gig.volunteer && !gig.fundedOpen && !selectsBeforePayment}
            selectsBeforePayment={selectsBeforePayment}
            requests={requests}
            teenDetailsByRequestId={teenDetailsByRequestId}
            loadingTeenRequestIds={loadingTeenRequestIds}
            highlightedRequestId={highlightedRequestId}
            approvingRequestId={approvingRequestId}
            rejectingRequestId={rejectingRequestId}
            schedulingRequestId={schedulingRequestId}
            onAcceptTeen={handleAcceptTeen}
            onRejectTeen={handleRejectTeen}
            onViewTeen={(_currentGigId, request) => handleOpenTeenModal(request)}
            onOpenConversation={handleOpenConversationModal}
          />
        )}
      </section>

      <Modal
        isOpen={selectedTeenRequest !== null}
        onClose={() => setSelectedTeenRequest(null)}
        title={selectedTeenRequest ? getTeenName(selectedTeenRequest) : "Teen"}
        panelClassName="max-w-4xl"
      >
        {selectedTeenRequest ? (
          <div className="space-y-4">
            <div className="rounded-2xl bg-myteengig-background px-4 py-4">
              <p className="text-sm text-myteengig-text-muted">
                Review the applicant profile, their note, and the time they proposed before making your decision.
              </p>
            </div>
            <TeenRequestProfileCard
              teenName={getTeenName(selectedTeenRequest)}
              teenDetail={teenDetailsByRequestId[selectedTeenRequest.requestId]}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl bg-myteengig-background px-4 py-4">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Teen message</p>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-myteengig-text-primary">
                  {selectedTeenRequest.message?.trim() || "No message included."}
                </p>
              </div>
              <div className="rounded-2xl bg-myteengig-background px-4 py-4">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Requested schedule</p>
                <p className="mt-2 text-sm font-medium text-myteengig-text-primary">
                  {formatDateRangeWithWeekdays(selectedTeenRequest.proposedStartTime, selectedTeenRequest.proposedEndTime, selectedTeenRequest.gigTimeZone)}
                </p>
              </div>
            </div>
          </div>
        ) : null}
      </Modal>

      <GigRequestConversationModal
        isOpen={selectedConversationRequest !== null}
        onClose={() => {
          setSelectedConversationRequest(null);
          setIsScheduleProposalComposerOpen(false);
          setScheduleProposalError(null);
        }}
        title={selectedConversationRequest ? `${getTeenName(selectedConversationRequest)} · Message / Suggest Time` : "Message / Suggest Time"}
        role="adult"
        requestId={selectedConversationRequest?.requestId ?? ""}
        participantLabel="teen"
        perspective="adult"
        proposals={selectedConversationProposals}
        timeZone={selectedConversationRequest?.gigTimeZone}
        summary={selectedConversationRequest ? (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-myteengig-text-primary">{getTeenName(selectedConversationRequest)}</p>
            <p className="text-sm text-myteengig-text-muted">
              Message this applicant and work through timing before you decide whether to accept them.
            </p>
            {selectedConversationRequest.requiresScheduleReproposal ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Your gig update changed the schedule window. This teen needs to send a new start time before you can accept them.
              </div>
            ) : null}
            {selectedConversationRequest.requiresGigChangeAcknowledgment ? (
              <div className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                The teen still needs to acknowledge your latest gig update before you can accept them.
              </div>
            ) : null}
            {selectedConversationRequest.teenConfirmedPosterTimeAt ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                This teen confirmed your latest suggested start time and is waiting on your decision.
              </div>
            ) : null}
          </div>
        ) : null}
        scheduleProposalContent={selectedConversationRequest && isScheduleProposalComposerOpen ? (
          <ScheduleProposalForm
            subtitle="Pick a new start time and MyTeenGig will keep the same gig length for the updated proposal."
            initialStartTime={selectedConversationRequest.proposedStartTime ?? null}
            initialEndTime={selectedConversationRequest.proposedEndTime ?? null}
            availabilityStartTime={selectedConversationRequest.gigStartTime ?? null}
            availabilityEndTime={selectedConversationRequest.gigEndTime ?? null}
            timeZone={selectedConversationRequest.gigTimeZone}
            allowOutsideAvailabilityWindow
            submitError={scheduleProposalError}
            isSubmitting={schedulingRequestId === selectedConversationRequest.requestId}
            onCancel={() => {
              setIsScheduleProposalComposerOpen(false);
              setScheduleProposalError(null);
            }}
            onSubmit={handleSubmitScheduleProposal}
          />
        ) : null}
        composerSecondaryAction={selectedConversationRequest ? (
          <Button
            variant="outline"
            disabled={schedulingRequestId === selectedConversationRequest.requestId}
            onClick={() => {
              setIsScheduleProposalComposerOpen((current) => !current);
              setScheduleProposalError(null);
            }}
          >
            {schedulingRequestId === selectedConversationRequest.requestId
              ? "Sending..."
              : isScheduleProposalComposerOpen
                ? "Hide Start Time Picker"
                : "Suggest New Start Time"}
          </Button>
        ) : null}
        emptyThreadMessage="No messages or timing updates yet."
        messagesDescription="Messages and timing changes stay together in this applicant thread."
      />

      <Modal
        isOpen={pendingRejection !== null}
        onClose={() => {
          if (rejectingRequestId === null) {
            setPendingRejection(null);
          }
        }}
        title={removingSelectedApplicant ? "Remove Selected Applicant?" : "Reject Applicant?"}
        panelClassName="max-w-lg"
      >
        {pendingRejection ? (
          <div className="space-y-4">
            <div className="rounded-2xl bg-myteengig-background px-4 py-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">
                {getTeenName(pendingRejection.request)}
              </p>
              <p className="mt-2 text-sm text-myteengig-text-muted">
                {removingSelectedApplicant
                  ? `This will remove the selected applicant before the gig is confirmed and reopen that spot for ${pendingRejection.gigTitle}.`
                  : `This will mark the request as declined and remove this applicant from the active applicant list for ${pendingRejection.gigTitle}.`}
              </p>
            </div>
            <p className="text-sm text-myteengig-text-muted">
              {removingSelectedApplicant
                ? "No payment or refund is involved because this applicant has not been funded."
                : "Are you sure you want to reject this applicant?"}
            </p>
            <div className="flex flex-wrap justify-end gap-3">
              <Button
                variant="outline"
                disabled={rejectingRequestId !== null}
                onClick={() => setPendingRejection(null)}
                className="w-full sm:w-auto"
              >
                {removingSelectedApplicant ? "Keep Selected" : "Keep Applicant"}
              </Button>
              <Button
                disabled={rejectingRequestId !== null}
                onClick={() => void handleConfirmRejection()}
                className="w-full sm:w-auto"
              >
                {rejectingRequestId !== null
                  ? removingSelectedApplicant ? "Removing..." : "Rejecting..."
                  : removingSelectedApplicant ? "Remove Selection" : "Yes, Reject"}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={pendingSelection !== null}
        onClose={() => {
          if (approvingRequestId === null && !isStartingFundingCheckout) {
            setPendingSelection(null);
          }
        }}
        title="Select Applicant"
        panelClassName="max-w-2xl"
      >
        {pendingSelection ? (
          <div className="space-y-5">
            <p className="text-sm text-myteengig-text-muted">
              Select <span className="font-semibold text-myteengig-text-primary">{pendingSelection.request.teenFirstName?.trim() || "this applicant"}</span> for{" "}
              <span className="font-semibold text-myteengig-text-primary">{pendingSelection.gigTitle}</span>.
            </p>

            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">What happens next</p>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                {pendingSelectionRemainingAfterSelection === 0 ? (
                  <>
                    <p>The teens below will be scheduled to arrive and complete the gig at <span className="font-semibold text-myteengig-text-primary">{pendingSelectionScheduleLabel}</span>.</p>
                    <ul className="list-disc space-y-1 pl-5">
                      {pendingSelectionCrew.map((request) => (
                        <li key={request.requestId}>{getTeenName(request)}</li>
                      ))}
                    </ul>
                    <p>MyTeenGig will hold the payment until the gig is marked complete, then release each teen's payout.</p>
                  </>
                ) : (
                  <>
                    <p>{pendingSelection.request.teenFirstName?.trim() || "This applicant"} will be added to the selected crew with the time shown on their request.</p>
                    <p>
                      The gig will stay posted until the remaining spot{pendingSelectionRemainingAfterSelection === 1 ? "" : "s"}{" "}
                      {pendingSelectionRemainingAfterSelection === 1 ? "is" : "are"} selected.
                    </p>
                  </>
                )}
              </div>
            </div>

            <PageActionBar align="between">
              <Button
                variant="outline"
                onClick={() => setPendingSelection(null)}
                disabled={approvingRequestId !== null || isStartingFundingCheckout}
                className="w-full sm:w-auto"
              >
                Not now
              </Button>
              <Button
                onClick={() => void handleConfirmSelection()}
                disabled={approvingRequestId !== null || isStartingFundingCheckout}
                className="w-full sm:w-auto"
              >
                {approvingRequestId !== null || isStartingFundingCheckout
                  ? pendingSelectionRemainingAfterSelection === 0 ? "Continuing to Stripe..." : "Selecting..."
                  : pendingSelectionRemainingAfterSelection === 0 ? "Select and continue to Stripe" : "Select applicant"}
              </Button>
            </PageActionBar>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={pendingProceedWithSelectedCrew !== null}
        onClose={() => {
          if (!isProceedingWithSelectedCrew) {
            setPendingProceedWithSelectedCrew(null);
          }
        }}
        title="Proceed With Selected Crew"
        panelClassName="max-w-2xl"
      >
        {pendingProceedWithSelectedCrew ? (
          <div className="space-y-5">
            <div className="space-y-2 text-sm text-myteengig-text-muted">
              <p>
                You originally requested <span className="font-semibold text-myteengig-text-primary">{pendingProceedWithSelectedCrew.originalTeensRequired}</span>{" "}
                teen{pendingProceedWithSelectedCrew.originalTeensRequired === 1 ? "" : "s"} for{" "}
                <span className="font-semibold text-myteengig-text-primary">{pendingProceedWithSelectedCrew.gigTitle}</span>.
              </p>
              <p>
                You currently have <span className="font-semibold text-myteengig-text-primary">{pendingProceedWithSelectedCrew.crewSeatCount}</span>{" "}
                {pendingProceedWithSelectedCrew.mode === "remaining" ? "active" : "selected"} teen{pendingProceedWithSelectedCrew.crewSeatCount === 1 ? "" : "s"}.
              </p>
            </div>

            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">What changes</p>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                {pendingProceedWithSelectedCrew.mode === "remaining" ? (
                  <p>
                    The crew size is changing from{" "}
                    <span className="font-semibold text-myteengig-text-primary">{pendingProceedWithSelectedCrew.originalTeensRequired}</span>{" "}
                    teen{pendingProceedWithSelectedCrew.originalTeensRequired === 1 ? "" : "s"} to{" "}
                    <span className="font-semibold text-myteengig-text-primary">{pendingProceedWithSelectedCrew.crewSeatCount}</span>{" "}
                    teen{pendingProceedWithSelectedCrew.crewSeatCount === 1 ? "" : "s"}
                    {pendingProceedWithSelectedCrew.projectedTeenPayoutCents !== null ? (
                      <>
                        , so each teen will now receive{" "}
                        <span className="font-semibold text-myteengig-text-primary">
                          {formatUsdFromCents(pendingProceedWithSelectedCrew.projectedTeenPayoutCents)}
                        </span>
                      </>
                    ) : null}
                    .
                  </p>
                ) : (
                  <>
                    <p>The requested crew size changes to the selected count.</p>
                    <p>The selected applicants stay selected.</p>
                    <p>No selected applicant is rejected or cancelled by this action.</p>
                    <p>No one is charged by this step.</p>
                  </>
                )}
              </div>
            </div>

            <PageActionBar align="between">
              <Button
                variant="outline"
                onClick={() => setPendingProceedWithSelectedCrew(null)}
                disabled={isProceedingWithSelectedCrew}
                className="w-full sm:w-auto"
              >
                {pendingProceedWithSelectedCrew.mode === "remaining"
                  ? `Keep waiting for ${pendingProceedWithSelectedCrew.spotsRemaining} more teen${pendingProceedWithSelectedCrew.spotsRemaining === 1 ? "" : "s"}`
                  : `Keep looking for ${pendingProceedWithSelectedCrew.spotsRemaining} more`}
              </Button>
              <Button
                onClick={() => void handleConfirmProceedWithSelectedCrew()}
                disabled={isProceedingWithSelectedCrew}
                className="w-full sm:w-auto"
              >
                {isProceedingWithSelectedCrew
                  ? "Updating..."
                  : `${pendingProceedWithSelectedCrew.mode === "remaining" ? "Continue" : "Proceed"} with ${pendingProceedWithSelectedCrew.crewSeatCount} teen${pendingProceedWithSelectedCrew.crewSeatCount === 1 ? "" : "s"}`}
              </Button>
            </PageActionBar>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={pendingCombinedFunding !== null}
        onClose={() => {
          if (isStartingFundingCheckout) {
            return null;
          }
          setPendingCombinedFunding(null);
          setFundingCheckoutError(null);
        }}
        title={(
          <span className="flex flex-wrap items-center gap-3">
            <span>Fund Selected Crew</span>
            {combinedFundingTotalChargeCents !== null ? (
              <span className="rounded-full border border-myteengig-border bg-white px-3 py-1 text-sm font-semibold text-myteengig-text-primary shadow-sm">
                {formatUsdFromCents(combinedFundingTotalChargeCents)}
              </span>
            ) : null}
          </span>
        )}
        panelClassName="max-w-2xl"
      >
        {pendingCombinedFunding ? (
          <div className="space-y-5">
            <div className="space-y-2 text-sm text-myteengig-text-muted">
              <p>
                This checkout confirms <span className="font-semibold text-myteengig-text-primary">{pendingCombinedFunding.selectedSeatCount}</span>{" "}
                selected teen{pendingCombinedFunding.selectedSeatCount === 1 ? "" : "s"} for{" "}
                <span className="font-semibold text-myteengig-text-primary">{pendingCombinedFunding.gigTitle}</span>.
              </p>
              {combinedFundingTotalChargeCents !== null ? (
                <p>
                  Stripe checkout will collect <span className="font-semibold text-myteengig-text-primary">{formatUsdFromCents(combinedFundingTotalChargeCents)}</span>{" "}
                  total before the crew is confirmed.
                </p>
              ) : null}
            </div>

            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-900">Funds are held until completion</p>
              <p className="mt-2 text-sm text-amber-900/90">
                Stripe checkout funds the selected crew now, but each teen is paid only after their scheduled gig is marked complete.
              </p>
            </div>

            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">What happens next</p>
              <div className="mt-2 space-y-2 text-sm text-myteengig-text-muted">
                <p>After payment succeeds, the selected applicants move into scheduled gigs.</p>
                <p>Other pending applicants are closed only after payment is confirmed.</p>
                <p>The payment is stored as one checkout with per-teen payout allocations.</p>
              </div>
            </div>

            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Agreement</p>
              <p className="mt-2 text-sm text-myteengig-text-muted">
                By continuing, you agree that MyTeenGig will collect and hold this payment until each scheduled gig assignment is marked complete.
              </p>
              <label className="mt-4 flex items-start gap-3 rounded-2xl bg-myteengig-background p-4">
                <input
                  type="checkbox"
                  checked={fundingAgreementAccepted}
                  onChange={(event) => setFundingAgreementAccepted(event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-myteengig-border"
                />
                <span className="text-sm text-myteengig-text-primary">
                  I agree to the funding terms and understand the selected crew is confirmed only after successful checkout.
                </span>
              </label>
            </div>

            {fundingCheckoutError ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-sm font-medium text-red-700">{fundingCheckoutError}</p>
              </div>
            ) : null}

            <PageActionBar align="between">
              <Button
                variant="outline"
                onClick={() => {
                  setPendingCombinedFunding(null);
                  setFundingCheckoutError(null);
                }}
                disabled={isStartingFundingCheckout}
                className="w-full sm:w-auto"
              >
                Not now
              </Button>
              <Button disabled={!fundingAgreementAccepted || isStartingFundingCheckout} onClick={() => void handleStartFundingCheckout()} className="w-full sm:w-auto">
                {isStartingFundingCheckout ? "Redirecting..." : "Continue to Stripe"}
              </Button>
            </PageActionBar>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={pendingFunding !== null}
        onClose={() => {
          if (isStartingFundingCheckout) {
            return null;
          }
          setPendingFunding(null);
          setFundingCheckoutError(null);
        }}
        title={(
          <span className="flex flex-wrap items-center gap-3">
            <span>
              {pendingFunding?.request.status === "POSTER_PENDING" && !pendingFunding.request.teenPayoutReady
                ? "Select Pending Payout Setup"
                : pendingFunding?.request.status === "FUNDING_PENDING"
                  ? "Pay to Confirm Gig"
                  : "Accept and Fund Gig"}
            </span>
            {pendingFunding ? (
              <span className="rounded-full border border-myteengig-border bg-white px-3 py-1 text-sm font-semibold text-myteengig-text-primary shadow-sm">
                {formatGigCompensation(pendingFunding.compensationCents, false)}
              </span>
            ) : null}
          </span>
        )}
        panelClassName="max-w-2xl"
      >
        {pendingFunding ? (
          <div className="space-y-5">
            <div className="space-y-2 text-sm text-myteengig-text-muted">
              <p>
                {pendingFunding.request.status === "POSTER_PENDING" && !pendingFunding.request.teenPayoutReady ? (
                  <>
                    You can select <span className="font-semibold text-myteengig-text-primary">{pendingFunding.request.teenFirstName?.trim() || "this teen"}</span>{" "}
                    now, but this gig will not be fully confirmed or funded until their payout setup is complete.
                  </>
                ) : (
                  <>
                    By accepting, you agree to pay <span className="font-semibold text-myteengig-text-primary">{pendingFunding.request.teenFirstName?.trim() || "this teen"}</span>{" "}
                    <span className="font-semibold text-myteengig-text-primary">{formatGigCompensation(pendingFunding.compensationCents, false)}</span> when{" "}
                    <span className="font-semibold text-myteengig-text-primary">{pendingFunding.gigTitle}</span> is marked complete by you.
                  </>
                )}
              </p>
            </div>

            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-900">
                {pendingFunding.request.status === "POSTER_PENDING" && !pendingFunding.request.teenPayoutReady
                  ? "No payment is collected yet"
                  : "Funds are held until completion"}
              </p>
              <p className="mt-2 text-sm text-amber-900/90">
                {pendingFunding.request.status === "POSTER_PENDING" && !pendingFunding.request.teenPayoutReady
                  ? "MyTeenGig will notify you when this teen's payout setup is ready so you can return and pay to fully confirm the gig."
                  : "Stripe checkout authorizes and funds the gig now, but the teen will not receive the payout until the gig is marked complete by you."}
              </p>
            </div>

            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Planned time window</p>
              <p className="mt-2 text-sm text-myteengig-text-muted">
                {formatDateRangeWithWeekdays(pendingFunding.request.proposedStartTime, pendingFunding.request.proposedEndTime, pendingFunding.request.gigTimeZone)}
              </p>
            </div>

            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">What happens next</p>
              <div className="mt-2 space-y-2 text-sm text-myteengig-text-muted">
                {pendingFunding.request.status === "POSTER_PENDING" && !pendingFunding.request.teenPayoutReady ? (
                  <>
                    <p>You have not paid yet. You can wait for this teen to finish payout setup or choose another applicant who is ready now.</p>
                    <p>After payout setup is ready, use Pay to confirm to move the gig into scheduled gigs.</p>
                  </>
                ) : (
                  <>
                    <p>Once payment is confirmed, this gig will move into your upcoming scheduled gigs.</p>
                    <p>The payment stays held in MyTeenGig until the scheduled gig is marked complete.</p>
                    <p>{pendingFunding.request.teenFirstName?.trim() || "The teen"} will then be expected to come during the confirmed time window shown above.</p>
                  </>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Agreement</p>
              <p className="mt-2 text-sm text-myteengig-text-muted">
                {pendingFunding.request.status === "POSTER_PENDING" && !pendingFunding.request.teenPayoutReady
                  ? "By continuing, you agree that this teen is conditionally selected and no payment will be collected until payout setup is complete and you return to pay."
                  : "By continuing, you agree that MyTeenGig will collect and hold this payment until the scheduled gig is marked complete."}
              </p>
              <label className="mt-4 flex items-start gap-3 rounded-2xl bg-myteengig-background p-4">
                <input
                  type="checkbox"
                  checked={fundingAgreementAccepted}
                  onChange={(event) => setFundingAgreementAccepted(event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-myteengig-border"
                />
                <span className="text-sm text-myteengig-text-primary">
                  {pendingFunding.request.status === "POSTER_PENDING" && !pendingFunding.request.teenPayoutReady
                    ? "I understand this selection is conditional, no payment is collected now, and the request is accepted only after payout setup is ready and checkout succeeds."
                    : "I agree to the funding terms and understand the request is accepted only after successful checkout, and the teen is paid only after the gig is marked complete."}
                </span>
              </label>
            </div>

            {fundingCheckoutError ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-sm font-medium text-red-700">{fundingCheckoutError}</p>
              </div>
            ) : null}

            <PageActionBar align="between">
              <Button
                variant="outline"
                onClick={() => {
                  setPendingFunding(null);
                  setFundingCheckoutError(null);
                }}
                disabled={isStartingFundingCheckout}
                className="w-full sm:w-auto"
              >
                Not now
              </Button>
              <Button disabled={!fundingAgreementAccepted || isStartingFundingCheckout} onClick={() => void handleStartFundingCheckout()} className="w-full sm:w-auto">
                {isStartingFundingCheckout
                  ? pendingFunding.request.status === "POSTER_PENDING" && !pendingFunding.request.teenPayoutReady
                    ? "Selecting..."
                    : "Redirecting..."
                  : pendingFunding.request.status === "POSTER_PENDING" && !pendingFunding.request.teenPayoutReady
                    ? "Select teen"
                    : "Continue to Stripe"}
              </Button>
            </PageActionBar>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={pendingVolunteerAcceptance !== null}
        onClose={() => {
          if (approvingRequestId) {
            return null;
          }
          setPendingVolunteerAcceptance(null);
        }}
        title="Accept Applicant"
        panelClassName="max-w-2xl"
      >
        {pendingVolunteerAcceptance ? (
          <div className="space-y-5">
            <p className="text-sm text-myteengig-text-muted">
              You are about to accept <span className="font-semibold text-myteengig-text-primary">{pendingVolunteerAcceptance.request.teenFirstName?.trim() || "this teen"}</span> for your gig.
            </p>

            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">What happens next</p>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                <p>The teen will be officially accepted for this gig right away.</p>
                <p>The latest proposed time on this request will become the confirmed schedule for the gig.</p>
                <p>This gig will move out of your posted gigs and into scheduled gigs.</p>
                <p>Any other open requests for this gig will be closed because the position is being filled.</p>
                <p>No payment will be collected because this gig is currently marked as a volunteer opportunity.</p>
              </div>
            </div>

            <PageActionBar align="between">
              <Button
                variant="outline"
                onClick={() => setPendingVolunteerAcceptance(null)}
                disabled={approvingRequestId !== null}
                className="w-full sm:w-auto"
              >
                Not now
              </Button>
              <Button
                onClick={() => void handleConfirmVolunteerAcceptance()}
                disabled={approvingRequestId !== null}
                className="w-full sm:w-auto"
              >
                {approvingRequestId !== null ? "Accepting..." : "Accept"}
              </Button>
            </PageActionBar>
          </div>
        ) : null}
      </Modal>
    </PageShell>
  );
};

export default AdultGigApplicantsPage;
